import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeftIcon } from 'lucide-react';
import AuthLayout from '../components/AuthLayout';
const OTPVerification = () => {
  const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));
  const navigate = useNavigate();
  const location = useLocation();
  const {
    verifyOtp
  } = useAuth();
  // Extract email from state passed during navigation
  const email = location.state?.email || '';
  const resetMode = location.state?.resetMode || false;
  useEffect(() => {
    // Focus the first input on component mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);
  const handleChange = (index: number, value: string) => {
    // Allow only digits
    if (!/^\d*$/.test(value)) return;
    // Update the OTP array
    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);
    // If value is entered and not the last input, move to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace: if empty, focus previous input
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle left arrow: focus previous input
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle right arrow: focus next input
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    // Check if pasted data is a 6-digit number
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setOtp(digits);
      // Focus the last input
      inputRefs.current[5]?.focus();
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }
    setIsLoading(true);
    try {
      const success = await verifyOtp(email, otpValue);
      if (success) {
        if (resetMode) {
          // If in password reset flow, navigate to reset password page
          navigate('/reset-password', {
            state: {
              email,
              otp: otpValue
            }
          });
        } else {
          // If in registration flow, navigate to home
          navigate('/');
        }
      } else {
        setError('Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const resendOtp = async () => {
    setError('');
    if (!email) {
      setError('Email information is missing. Please go back and try again.');
      return;
    }
    setIsLoading(true);
    try {
      // Call the resend OTP function from auth context
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Show success message
      setError('OTP has been resent to your email');
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  return <AuthLayout title="Verification Required" subtitle="Confirm your identity" data-id="element-687">
      <div className="mt-8 space-y-6" data-id="element-688">
        <div data-id="element-689">
          <Link to={resetMode ? '/forgot-password' : '/signup'} className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors mb-6" data-id="element-690">
            <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-691" />
            Back
          </Link>
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-692">
            Verification Code
          </h2>
          <p className="mt-2 text-sm text-gray-600" data-id="element-693">
            We've sent a 6-digit code to{' '}
            <span className="font-medium text-indigo-600" data-id="element-694">
              {email || 'your email'}
            </span>
          </p>
        </div>
        {error && <div className={`p-4 rounded-md ${error.includes('resent') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`} data-id="element-695">
            {error}
          </div>}
        <form className="space-y-6" onSubmit={handleSubmit} data-id="element-696">
          <div className="flex justify-between space-x-2" data-id="element-697">
            {[0, 1, 2, 3, 4, 5].map(index => <input key={index} ref={el => inputRefs.current[index] = el} type="text" maxLength={1} value={otp[index]} onChange={e => handleChange(index, e.target.value)} onKeyDown={e => handleKeyDown(index, e)} onPaste={index === 0 ? handlePaste : undefined} className="w-full h-14 text-center text-xl font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" required data-id="element-698" />)}
          </div>
          <div className="text-center" data-id="element-699">
            <p className="text-sm text-gray-600" data-id="element-700">
              Didn't receive the code?{' '}
              <button type="button" onClick={resendOtp} className="font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none" disabled={isLoading} data-id="element-701">
                Resend
              </button>
            </p>
          </div>
          <div data-id="element-702">
            <button type="submit" disabled={isLoading} className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-all disabled:opacity-70" data-id="element-703">
              {isLoading ? <span className="flex items-center" data-id="element-704">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-705">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-706"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-707"></path>
                  </svg>
                  Verifying...
                </span> : 'Verify'}
            </button>
          </div>
        </form>
      </div>
    </AuthLayout>;
};
export default OTPVerification;